/**
 * Phase 78 — Season Transition Wizard.
 *
 * Three steps:
 *  1. Pick athletes from the source (current) season and assign a
 *     transition decision (staying / leaving / trial / uncertain) plus
 *     optional next squad / next status overrides.
 *  2. Preview — backend computes the summary (counts of carry-forward
 *     injuries, career pathways, baselines, and PDPs that will be
 *     archived).
 *  3. Confirm — backend creates the new season (Aug→Jul, +1 year),
 *     atomically inserts new athlete_season_links, archives prior PDPs,
 *     and promotes the new season to current.
 *
 * Active injuries, career pathways, academic profile and documents are
 * already linked to athlete_id (not the link row) so they remain visible
 * with zero migration — the preview only counts them so staff see what
 * carries forward.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, ArrowRight, Calendar, CheckCircle2, Users, AlertTriangle,
  ShieldAlert, Award, Activity, Archive,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];
const DECISIONS = [
  { value: 'staying',   label: 'Staying',   tone: 'bg-emerald-100 text-emerald-700' },
  { value: 'trial',     label: 'Trial Only', tone: 'bg-blue-100 text-blue-700' },
  { value: 'uncertain', label: 'Uncertain', tone: 'bg-amber-100 text-amber-700' },
  { value: 'leaving',   label: 'Leaving',   tone: 'bg-red-100 text-red-700' },
];

// Phase 82.1 — Surface structured backend errors instead of a generic
// "Transition failed". Accepts an axios error and returns:
//   { headline, failed_stage?, details?, raw }
// where `headline` is always populated (never empty) and `raw` is the
// JSON-stringified payload for "Copy technical details".
const parseTransitionError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail;

  // Backend returns structured { error, message, failed_stage, details } in detail.
  if (detail && typeof detail === 'object') {
    return {
      headline: detail.message || detail.error || 'Transition failed.',
      failed_stage: detail.failed_stage,
      athlete_id: detail.athlete_id,
      details: Array.isArray(detail.details) ? detail.details : null,
      status,
      raw: JSON.stringify(data, null, 2),
    };
  }
  // FastAPI default validation errors → array of {loc, msg, type}
  if (Array.isArray(detail)) {
    const first = detail[0];
    return {
      headline: first?.msg ? `Backend validation error: ${first.msg}` : 'Validation error',
      failed_stage: 'validation',
      details: detail,
      status,
      raw: JSON.stringify(data, null, 2),
    };
  }
  // Plain-string detail (legacy 4xx)
  if (typeof detail === 'string' && detail.trim()) {
    return { headline: detail, status, raw: JSON.stringify(data, null, 2) };
  }
  // Server returned no detail at all — surface the HTTP status.
  return {
    headline: status === 500
      ? 'Internal server error during transition — see technical details.'
      : err?.message || 'Transition failed.',
    status,
    raw: JSON.stringify(data || { message: err?.message }, null, 2),
  };
};

const SeasonTransitionWizard = () => {
  const navigate = useNavigate();
  // AuthContext doesn't expose the token quickly enough on direct navigation
  // to /seasons/transition, so we read the canonical token from
  // localStorage — same pattern as SeasonReportPrint and other pages.
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(1);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [selections, setSelections] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [executing, setExecuting] = useState(false);
  // Phase 82.1 — Structured execute-error banner shown in-page so the
  // admin can read the real backend message (and copy the technical
  // payload for support) instead of relying on a generic toast.
  const [executeError, setExecuteError] = useState(null);

  // Load source season + its active athletes.
  const bootstrap = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const cur = await axios.get(`${API}/api/seasons/current`, { headers });
      setCurrentSeason(cur.data);
      const links = await axios.get(`${API}/api/seasons/${cur.data.id}/athletes`, { headers });
      // Enrich each link with its athlete profile.
      const ath = await axios.get(`${API}/api/athletes`, { headers });
      const ath_by_id = Object.fromEntries(ath.data.map(a => [a.id, a]));
      const enriched = (links.data || [])
        .filter(l => l.status === 'active' || l.status === 'trial')
        .map(l => {
          const a = ath_by_id[l.athlete_id] || {};
          return {
            id: l.athlete_id,
            full_name: a.full_name,
            date_of_birth: a.date_of_birth,
            nationality: a.nationality,
            squad: l.squad || a.squad,
            status: l.status,
          };
        })
        .filter(a => a.full_name);
      setAthletes(enriched);
      // Default all to "staying" so the wizard starts with the expected behavior.
      const initial = {};
      enriched.forEach(a => { initial[a.id] = { decision: 'staying', next_squad: a.squad, next_status: 'active' }; });
      setSelections(initial);
    } catch (err) { toast.error('Failed to load season data'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const filtered = athletes.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.full_name || '').toLowerCase().includes(s)
      || (a.squad || '').toLowerCase().includes(s)
      || (a.nationality || '').toLowerCase().includes(s);
  });

  const setDecision = (id, decision) => {
    setSelections(curr => ({ ...curr, [id]: { ...curr[id], decision,
      next_status: decision === 'staying' ? 'active' : decision === 'trial' ? 'trial' : decision === 'uncertain' ? 'uncertain' : 'left',
    } }));
  };
  const setNextSquad = (id, next_squad) => setSelections(curr => ({ ...curr, [id]: { ...curr[id], next_squad } }));

  // Phase 7 — PDP carry-over state.
  const [pdpPreview, setPdpPreview] = useState(null);       // {athletes: [...]}
  const [carryOverByAthlete, setCarryOverByAthlete] = useState({}); // {athleteId: Set<goalId>}
  const [expandedAthletes, setExpandedAthletes] = useState({});

  const buildPayload = () => ({
    selections: athletes.map(a => ({
      athlete_id: a.id,
      decision: selections[a.id]?.decision || 'staying',
      next_squad: selections[a.id]?.next_squad || a.squad,
      next_status: selections[a.id]?.next_status || null,
      goals_to_carry_over: Array.from(carryOverByAthlete[a.id] || []),
    })),
  });

  const runPreview = async () => {
    if (!currentSeason) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/seasons/${currentSeason.id}/transition-preview`,
        buildPayload(), { headers }
      );
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      const parsed = parseTransitionError(err);
      toast.error(`Preview failed: ${parsed.headline}`);
    }
    finally { setLoading(false); }
  };

  const runPdpPreview = async () => {
    if (!currentSeason) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/seasons/${currentSeason.id}/transition-pdp-preview`,
        buildPayload(), { headers }
      );
      setPdpPreview(res.data);
      // Reset carry-over selections each time the preview is loaded (spec:
      // "No goal should be selected automatically.").
      setCarryOverByAthlete({});
      setStep(3);
    } catch (err) {
      const parsed = parseTransitionError(err);
      toast.error(`PDP preview failed: ${parsed.headline}`);
    }
    finally { setLoading(false); }
  };

  const toggleGoal = (athleteId, goalId) => {
    setCarryOverByAthlete(prev => {
      const next = { ...prev };
      const cur = new Set(next[athleteId] || []);
      if (cur.has(goalId)) cur.delete(goalId);
      else cur.add(goalId);
      next[athleteId] = cur;
      return next;
    });
  };

  const selectAllForAthlete = (athleteId, goals) => {
    setCarryOverByAthlete(prev => ({
      ...prev,
      [athleteId]: new Set(goals.map(g => g.id)),
    }));
  };

  const clearAllForAthlete = (athleteId) => {
    setCarryOverByAthlete(prev => ({ ...prev, [athleteId]: new Set() }));
  };

  const totalGoalsToCarry = Object.values(carryOverByAthlete).reduce(
    (sum, s) => sum + (s?.size || 0), 0
  );

  const execute = async () => {
    if (!currentSeason) return;
    setExecuting(true);
    setExecuteError(null);
    try {
      const res = await axios.post(
        `${API}/api/seasons/${currentSeason.id}/transition-execute`,
        buildPayload(), { headers }
      );
      toast.success(`Transitioned to ${res.data.target_season.name}: ${res.data.created_links} new links, ${res.data.archived_pdps} PDPs archived${totalGoalsToCarry > 0 ? `, ${totalGoalsToCarry} goal(s) carried over` : ''}.`);
      setStep(4);
    } catch (err) {
      const parsed = parseTransitionError(err);
      setExecuteError(parsed);
      toast.error(parsed.headline);
    }
    finally { setExecuting(false); }
  };

  const bulkApply = (decision) => {
    const next = {};
    athletes.forEach(a => {
      next[a.id] = {
        decision,
        next_squad: selections[a.id]?.next_squad || a.squad,
        next_status: decision === 'staying' ? 'active' : decision === 'trial' ? 'trial' : decision === 'uncertain' ? 'uncertain' : 'left',
      };
    });
    setSelections(next);
  };

  // ── Step 4: success screen ───────────────────────────────────────────
  if (step === 4) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center" data-testid="transition-success">
          <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Transition complete</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            The new season is now active. Previous-season data remains viewable as history.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => navigate('/operations/seasons')} data-testid="transition-back-to-seasons">
              Back to Seasons
            </Button>
            <Button className="bg-pfap-green hover:bg-pfap-green/90 text-white" onClick={() => navigate('/admin/athletes')} data-testid="transition-go-to-athletes">
              View Athletes
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="transition-wizard">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Calendar size={20} className="text-pfap-green" /> Transition to New Season
            </h1>
            {currentSeason && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Source: <b>{currentSeason.name}</b>. Target season will be created automatically (Aug 1 → Jul 31, +1 year).
              </p>
            )}
          </div>
          <Button variant="outline" onClick={() => navigate('/operations/seasons')} data-testid="transition-cancel">
            Cancel
          </Button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 text-xs">
          {[1, 2, 3, 4].map((n) => (
            <div key={n}
                 className={`flex items-center gap-1.5 px-2 py-1 rounded ${step >= n ? 'bg-pfap-green/10 text-pfap-green' : 'bg-gray-100 text-gray-400 dark:bg-gray-700'}`}>
              <span className="font-semibold">{n}</span>
              <span>{n === 1 ? 'Pick athletes' : n === 2 ? 'Preview' : n === 3 ? 'PDP carry-over' : 'Confirm'}</span>
            </div>
          ))}
        </div>

        {step === 1 && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="transition-step1">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Users size={16} className="text-pfap-green" /> {athletes.length} active athletes in current season
                </CardTitle>
                <div className="flex items-center gap-1.5" data-testid="transition-bulk-actions">
                  {DECISIONS.map(d => (
                    <Button key={d.value} size="sm" variant="outline" className="h-7 text-[11px] px-2"
                            onClick={() => bulkApply(d.value)}
                            data-testid={`transition-bulk-${d.value}`}>
                      All {d.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Input
                placeholder="Search by name, squad or nationality…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-3"
                data-testid="transition-search"
              />
              {loading ? (
                <p className="text-sm text-gray-500" data-testid="transition-loading">Loading…</p>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-3 py-2">Athlete</th>
                        <th className="text-left px-3 py-2">Current squad</th>
                        <th className="text-left px-3 py-2">Decision</th>
                        <th className="text-left px-3 py-2">Next squad</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {filtered.map(a => {
                        const sel = selections[a.id] || { decision: 'staying' };
                        return (
                          <tr key={a.id} data-testid={`transition-row-${a.id}`}>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{a.full_name}</div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">DOB {a.date_of_birth || '—'} · {a.nationality || '—'}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{a.squad || '—'}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1 flex-wrap">
                                {DECISIONS.map(d => (
                                  <button key={d.value} type="button"
                                          onClick={() => setDecision(a.id, d.value)}
                                          className={`px-2 py-0.5 rounded text-[10px] ${sel.decision === d.value ? d.tone : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
                                          data-testid={`transition-decision-${a.id}-${d.value}`}>
                                    {d.label}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <select value={sel.next_squad || ''}
                                      onChange={(e) => setNextSquad(a.id, e.target.value)}
                                      disabled={sel.decision === 'leaving'}
                                      className="h-7 px-2 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-[11px]"
                                      data-testid={`transition-next-squad-${a.id}`}>
                                <option value="">— same —</option>
                                {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                        onClick={runPreview} disabled={loading || athletes.length === 0}
                        data-testid="transition-go-to-preview">
                  Continue <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && preview && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="transition-step2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100">Preview transition</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                <p className="text-xs text-emerald-800 dark:text-emerald-200">
                  Target season: <b data-testid="transition-target-name">{preview.target_season?.name}</b>
                  {' '}({preview.target_season?.start_date} → {preview.target_season?.end_date})
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="transition-preview-summary">
                <SummaryTile icon={Users} label="Selected"  value={preview.summary.selected_total} />
                <SummaryTile icon={CheckCircle2} label="Staying" value={preview.summary.staying} tone="emerald" />
                <SummaryTile icon={Activity} label="Trial"   value={preview.summary.trial} tone="blue" />
                <SummaryTile icon={AlertTriangle} label="Uncertain" value={preview.summary.uncertain} tone="amber" />
                <SummaryTile icon={ShieldAlert} label="Active injuries carry forward" value={preview.summary.active_injuries_carry_forward} tone="red" />
                <SummaryTile icon={Award} label="Active career pathways carry forward" value={preview.summary.active_career_pathways_carry_forward} tone="purple" />
                <SummaryTile icon={Archive} label="PDPs to archive" value={preview.summary.pdps_to_archive} tone="gray" />
                <SummaryTile icon={Activity} label="Baselines linked"
                             value={`${preview.summary.athletes_with_biometric_baseline} bio · ${preview.summary.athletes_with_fitness_baseline} fit`} tone="blue" />
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                Active injuries, career pathways, academic profile and documents remain linked to the athlete and stay visible automatically — no copy needed. Previous-season match reports, attendance, training programmes, completed PDP goals and old season reports remain in history (filter by previous season to view them).
              </div>
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="transition-back">
                  <ArrowLeft size={14} className="mr-1.5" /> Back to selection
                </Button>
                <Button className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                        onClick={runPdpPreview} disabled={loading}
                        data-testid="transition-next-pdp">
                  {loading ? 'Loading…' : 'Next — PDP carry-over →'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="transition-step3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Optional: pick PDP goals to carry forward
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                The new season starts clean by default. Tick any unfinished goals you want continued in the new PDP cycle. Completed and archived goals never appear. <b>{totalGoalsToCarry}</b> goal(s) currently selected.
              </div>

              {!pdpPreview || pdpPreview.athletes.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500" data-testid="pdp-step-empty">
                  No athletes available for carry-over.
                </div>
              ) : (
                <div className="space-y-2" data-testid="pdp-carry-list">
                  {pdpPreview.athletes.map(a => {
                    const expanded = !!expandedAthletes[a.athlete_id];
                    const picked = carryOverByAthlete[a.athlete_id] || new Set();
                    const hasGoals = (a.goal_count || 0) > 0;
                    return (
                      <div key={a.athlete_id}
                           className="border border-gray-200 dark:border-gray-700 rounded-md"
                           data-testid={`pdp-athlete-${a.athlete_id}`}>
                        <button
                          type="button"
                          onClick={() => setExpandedAthletes(prev => ({ ...prev, [a.athlete_id]: !expanded }))}
                          className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          data-testid={`pdp-athlete-toggle-${a.athlete_id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{a.full_name}</span>
                            {a.squad && <span className="text-[10px] text-gray-500">· {a.squad}</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {hasGoals
                              ? <span data-testid={`pdp-athlete-count-${a.athlete_id}`}>{picked.size}/{a.goal_count} picked</span>
                              : <span className="italic">No unfinished PDP goals</span>}
                            <span>{expanded ? '▾' : '▸'}</span>
                          </div>
                        </button>
                        {expanded && hasGoals && (
                          <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 space-y-2">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                      onClick={() => selectAllForAthlete(a.athlete_id, a.goals)}
                                      data-testid={`pdp-select-all-${a.athlete_id}`}>
                                Select all
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                                      onClick={() => clearAllForAthlete(a.athlete_id)}
                                      data-testid={`pdp-clear-all-${a.athlete_id}`}>
                                Clear all
                              </Button>
                            </div>
                            {/* Group by category for readability. */}
                            {['Technical', 'Tactical', 'Physical', 'Mental', 'Social'].map(cat => {
                              const inCat = (a.goals || []).filter(g => g.category === cat);
                              if (inCat.length === 0) return null;
                              return (
                                <div key={cat}>
                                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{cat}</div>
                                  <ul className="space-y-1">
                                    {inCat.map(g => (
                                      <li key={g.id} className="flex items-start gap-2 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={picked.has(g.id)}
                                          onChange={() => toggleGoal(a.athlete_id, g.id)}
                                          className="mt-0.5"
                                          data-testid={`pdp-goal-${g.id}`}
                                        />
                                        <div className="min-w-0">
                                          <div className="text-gray-900 dark:text-gray-100 font-medium">
                                            {g.title}
                                          </div>
                                          {g.description && (
                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                              {g.description}
                                            </div>
                                          )}
                                          <div className="text-[10px] text-gray-400">
                                            {g.cycle_name || '—'} · progress {g.progress || 0}%
                                            {g.priority ? ` · ${g.priority}` : ''}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="transition-back-to-preview">
                  <ArrowLeft size={14} className="mr-1.5" /> Back to preview
                </Button>
                <Button className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                        onClick={execute} disabled={executing}
                        data-testid="transition-confirm">
                  {executing
                    ? 'Transitioning…'
                    : totalGoalsToCarry > 0
                      ? `Confirm — carry ${totalGoalsToCarry} goal(s)`
                      : 'Confirm — no carry-over'}
                </Button>
              </div>

              {executeError && (
                <TransitionErrorBanner
                  parsed={executeError}
                  onRetry={execute}
                  retrying={executing}
                  onDismiss={() => setExecuteError(null)}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

// Phase 82.1 — Persistent in-page error surface for transition-execute
// failures. Shows the real backend message, the failed stage, the
// affected athlete (if any), and a "Copy technical details" button
// for support. Retry is safe because the backend is idempotent.
const TransitionErrorBanner = ({ parsed, onRetry, retrying, onDismiss }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const stageLabel = parsed.failed_stage
    ? parsed.failed_stage.replace(/_/g, ' ')
    : null;
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(parsed.raw || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div
      className="mt-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-800/50 p-3 space-y-2"
      data-testid="transition-error-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-800 dark:text-red-200" data-testid="transition-error-headline">
            Transition failed{stageLabel ? ` at ${stageLabel} stage` : ''}.
          </p>
          <p className="text-xs text-red-700 dark:text-red-300 mt-0.5 break-words" data-testid="transition-error-message">
            {parsed.headline}
          </p>
          {parsed.athlete_id && (
            <p className="text-[11px] text-red-700/80 dark:text-red-300/80 mt-1">
              Affected athlete: <code className="font-mono">{parsed.athlete_id}</code>
            </p>
          )}
          {parsed.status && (
            <p className="text-[10px] text-red-600/70 dark:text-red-400/70 mt-0.5">
              HTTP {parsed.status}
            </p>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-red-600 hover:text-red-800 text-xs"
          data-testid="transition-error-dismiss"
        >
          Dismiss
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          disabled={retrying}
          className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300"
          data-testid="transition-error-retry"
        >
          {retrying ? 'Retrying…' : 'Retry (safe — idempotent)'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setExpanded(v => !v)}
          className="h-7 text-xs"
          data-testid="transition-error-toggle-details"
        >
          {expanded ? 'Hide' : 'Show'} technical details
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={copyDetails}
          className="h-7 text-xs"
          data-testid="transition-error-copy"
        >
          {copied ? 'Copied' : 'Copy technical details'}
        </Button>
      </div>
      {expanded && (
        <pre
          className="mt-2 max-h-56 overflow-auto rounded bg-red-100 dark:bg-red-950/50 p-2 text-[10px] font-mono text-red-900 dark:text-red-200 whitespace-pre-wrap break-all"
          data-testid="transition-error-payload"
        >
          {parsed.raw}
        </pre>
      )}
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value, tone = 'gray' }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
    blue:    'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    amber:   'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    red:     'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    purple:  'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    gray:    'bg-gray-50 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300',
  };
  return (
    <div className={`p-2 rounded-md ${tones[tone]}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={12} />
        <p className="text-[10px] font-medium uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-lg font-bold mt-0.5">{value}</p>
    </div>
  );
};

export default SeasonTransitionWizard;
